import { useCallback, useMemo, useState } from "react";
import { ConversationImageAnalysisService } from "@/services/agent/vision/ConversationImageAnalysisService";
import { fileToCompressedBase64 } from "@/utils/image/toBase64";

export type ImageAttachmentStatus = "analyzing" | "ready" | "error";

export type ImageAttachment = {
  id: string;
  file: File;
  previewUrl: string;
  status: ImageAttachmentStatus;
  analysisText?: string;
  compressedBase64?: string | null;
};

export const MAX_IMAGE_ATTACHMENTS = 10;

const createObjectUrlSafely = (file: File): string => {
  try {
    return URL.createObjectURL(file);
  } catch (error) {
    console.warn("[useImageAttachments] Failed to create object URL:", error);
    return "";
  }
};

const revokeObjectUrlSafely = (url: string) => {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch (error) {
    console.warn("[useImageAttachments] Failed to revoke object URL:", error);
  }
};

export const useImageAttachments = (maxAttachments = MAX_IMAGE_ATTACHMENTS) => {
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);

  const hasAnalyzingAttachment = useMemo(
    () => attachments.some((a) => a.status === "analyzing"),
    [attachments],
  );

  const analyzeAttachment = useCallback(async (file: File, id: string) => {
    const base64 = await fileToCompressedBase64(file);
    if (!base64) {
      setAttachments((prev) =>
        prev.map((att) =>
          att.id === id
            ? {
                ...att,
                status: "error",
              }
            : att,
        ),
      );
      return;
    }

    const result = await ConversationImageAnalysisService.analyzeImage(base64);

    setAttachments((prev) =>
      prev.map((att) => {
        if (att.id !== id) return att;
        if (!result) {
          return {
            ...att,
            status: "error",
            compressedBase64: base64,
          };
        }
        return {
          ...att,
          status: "ready",
          analysisText: result.descriptionText,
          compressedBase64: base64,
        };
      }),
    );
  }, []);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const next: ImageAttachment[] = [];
      const availableSlots = Math.max(0, maxAttachments - attachments.length);
      Array.from(files).slice(0, availableSlots).forEach((file) => {
        if (!file.type.startsWith("image/")) return;
        const id = `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
        next.push({
          id,
          file,
          previewUrl: createObjectUrlSafely(file),
          status: "analyzing",
        });

        void analyzeAttachment(file, id);
      });

      if (next.length > 0) {
        setAttachments((prev) => [...prev, ...next]);
      }
    },
    [analyzeAttachment, attachments.length, maxAttachments],
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      prev.forEach((att) => {
        if (att.id === id) {
          revokeObjectUrlSafely(att.previewUrl);
        }
      });
      return prev.filter((att) => att.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((att) => revokeObjectUrlSafely(att.previewUrl));
      return [];
    });
  }, []);

  const updateAttachmentAnalysis = useCallback((id: string, text: string) => {
    setAttachments((prev) =>
      prev.map((att) =>
        att.id === id
          ? {
              ...att,
              analysisText: text,
              status: text.trim().length > 0 ? "ready" : att.status,
            }
          : att,
      ),
    );
  }, []);

  const consumeReadyContext = useCallback((): string | undefined => {
    const readyTexts = attachments
      .filter((att) => att.status === "ready" && att.analysisText && att.analysisText.trim().length > 0)
      .map((att) => att.analysisText!.trim());

    if (readyTexts.length === 0) {
      return undefined;
    }

    return readyTexts.join("\n\n");
  }, [attachments]);

  return {
    attachments,
    addFiles,
    removeAttachment,
    clearAll,
    hasAnalyzingAttachment,
    updateAttachmentAnalysis,
    consumeReadyContext,
  };
};


